import ar from './messages/ar.json';
import en from './messages/en.json';
import { DEFAULT_LOCALE, type Locale } from './locale';

/**
 * The catalogues, statically imported.
 *
 * Both languages are in the bundle rather than fetched, and the arithmetic
 * settles it: 213 keys is a few kilobytes, and a dynamic import would trade that
 * for a loading state on every screen and a locale switch that flickers. When
 * the Phase-4 expansion (C9) adds three more languages this becomes a
 * `import()` per locale; at two it would be ceremony.
 */
type Messages = { readonly [key: string]: unknown };

const CATALOGUES: Record<Locale, Messages> = { ar, en };

/**
 * Looks up `admin.sidebar.talents` and fills `{count}` style placeholders.
 *
 * A missing key returns the key itself. Not a thrown error — a console that
 * refuses to render because one label is missing is worse than one that shows
 * `admin.sidebar.talents` — and not an empty string, because a blank space is a
 * bug nobody reports. The key on screen names the thing to fix.
 */
export interface Translate {
  (key: string, values?: Record<string, string | number>): string;

  /**
   * The node under `key`, whatever shape it has.
   *
   * For the handful of places a catalogue holds *structured* copy rather than a
   * sentence — the component gallery's demo rows, which carry translated names
   * alongside a tone and a count. Putting those in the message file is right;
   * they are text a translator edits. Reaching them through `t()` is not, and
   * this is the escape hatch `next-intl` called the same thing.
   *
   * Untyped by nature, so the caller asserts. There are two call sites.
   */
  raw: (key: string) => unknown;
}

export function translatorFor(locale: Locale): Translate {
  const catalogue = CATALOGUES[locale] ?? CATALOGUES[DEFAULT_LOCALE];

  const translate = ((key, values) => {
    const template = lookup(catalogue, key) ?? lookup(CATALOGUES[DEFAULT_LOCALE], key);
    if (template === undefined) {
      return key;
    }

    return values === undefined ? template : interpolate(template, values);
  }) as Translate;

  translate.raw = (key) => node(catalogue, key) ?? node(CATALOGUES[DEFAULT_LOCALE], key);

  return translate;
}

/**
 * `t` narrowed to one namespace: `scopeTranslator(t, 'admin.nav')('talents')` reads
 * `admin.nav.talents`. The same `raw` escape hatch, scoped the same way.
 */
export function scopeTranslator(t: Translate, namespace: string): Translate {
  const scoped = ((key, values) => t(`${namespace}.${key}`, values)) as Translate;
  scoped.raw = (key) => t.raw(`${namespace}.${key}`);
  return scoped;
}

/** Walks to whatever sits under `key` — a string, an object, an array, or nothing. */
function node(messages: Messages, key: string): unknown {
  let current: unknown = messages;

  for (const segment of key.split('.')) {
    if (typeof current !== 'object' || current === null) return undefined;
    current = (current as Messages)[segment];
  }

  return current;
}

function lookup(messages: Messages, key: string): string | undefined {
  // Only a leaf string is a translation. Landing on a nested object means the
  // key names a namespace, which is a caller bug and reads as the key on screen.
  const found = node(messages, key);
  return typeof found === 'string' ? found : undefined;
}

/**
 * `{name}` and nothing more elaborate.
 *
 * ICU plurals are what a full library buys, and nothing in these catalogues uses
 * one — Arabic has six plural forms and the copy is written to avoid needing
 * them, which is a translation decision rather than a limitation of this
 * function. If that changes, this is where the library goes.
 */
function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in values ? String(values[name]) : whole,
  );
}
